/**
 * Pre-compiled Extension Loader
 *
 * Loads pre-compiled JavaScript components via es-module-shims
 * (fetch text → Blob URL → importShim). The dependencies a component imports
 * (Ember, decorator-transforms, host components, …) are resolved through the
 * custom resolve/fetch hooks below, backed by modules the host registers via
 * ImportMapService.
 *
 * Replaces the previous WASM + Babel + eval() pipeline.
 */

import capabilities from './capabilities.ts';

interface EsmGlobalThis {
  importShim?: (url: string) => Promise<unknown>;
  esmsInitOptions?: unknown;
}

interface LoadResult {
  component: unknown;
  error?: Error;
}

class PrecompiledExtensionLoader {
  // Generated extension module source reaches the cache through this global key.
  #moduleCacheKey = '__peekExtensionModuleCache__';

  /**
   * URL for es-module-shims, loaded via a <script> tag (NOT bundled).
   *
   * CRITICAL: es-module-shims MUST NOT be processed by Webpack. Internally it
   * executes composed modules with native `import()` calls (e.g. `import(load.u)`
   * where `load.u` is a blob: URL). When bundled via `await import(...)`, Webpack
   * rewrites those into its own dynamic-import runtime, which then fails to
   * resolve the blob: URL with "Cannot find module 'blob:...'". A classic script
   * keeps those `import()` calls native.
   */
  #esModuleShimsUrl =
    'https://cdn.jsdelivr.net/npm/es-module-shims@2.8.1/dist/es-module-shims.js';

  // Dependency getter — hard-private and never on globalThis, so only the addon
  // can register or read it (set-once: a later call cannot replace it).
  #getter: ((moduleName: string) => unknown) | undefined;

  // Holds non-serializable dependency values (BigInt, functions, …). It must be
  // reachable from the dynamically generated extension module source, which runs
  // in its own module realm and can only bridge back through a global — so it is
  // also exposed as a locked, non-enumerable global property (see #installCache).
  #cache = new Map<string, unknown>();
  #cacheCounter = 0;

  // Compiled source of the extension(s) currently loading, keyed by a synthetic
  // id and served to es-module-shims via the `ext-main:` scheme — so it never
  // has to fetch a blob: URL (which strict CSP connect-src blocks).
  #mainSources = new Map<string, string>();
  #mainCounter = 0;

  // importShim module URL of a loading extension -> origin it was fetched from,
  // so its capabilities module can scope `fetch` to that origin.
  #moduleOrigins = new Map<string, string>();

  #esmsLoadPromise: Promise<void> | undefined;

  constructor() {
    // Both must run synchronously at module load, before es-module-shims loads.
    this.#installModuleCache();
    this.#registerHooks();
  }

  /** Register the dependency getter (called once by ImportMapService). */
  setDependencyGetter(getter: (moduleName: string) => unknown): void {
    this.#getter ??= getter;
  }

  /**
   * Load a pre-compiled extension from a URL.
   *
   * @param url - URL to fetch the pre-compiled component from
   * @returns The default export of the component, or an error
   */
  async load(url: string): Promise<LoadResult> {
    try {
      // Fetch the component first so an HTTP error fails fast, before we bother
      // loading es-module-shims.
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch ${url}: ${response.statusText}`);
      }

      const compiledText = await response.text();
      if (!compiledText) {
        throw new Error(`Empty response from ${url}`);
      }

      await this.#loadEsModuleShims();

      // Serve the component to es-module-shims via a virtual `ext-main:` URL
      // (handled by the fetch hook) instead of a blob: URL. Importing a blob
      // makes es-module-shims fetch() it, which strict CSP blocks under
      // connect-src.
      const sourceId = `main_${this.#mainCounter++}`;
      const moduleUrl = `ext-main:${sourceId}`;
      this.#mainSources.set(sourceId, compiledText);
      this.#moduleOrigins.set(moduleUrl, this.#originOf(url));

      try {
        const module = (await this.#global.importShim!(moduleUrl)) as {
          default?: unknown;
        };

        if (!module.default) {
          throw new Error(
            `Module from ${url} does not export a default export`
          );
        }

        return { component: module.default };
      } finally {
        this.#mainSources.delete(sourceId);
        this.#moduleOrigins.delete(moduleUrl);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      return {
        component: undefined,
        error: new Error(
          `Failed to load pre-compiled extension: ${errorMessage}`
        )
      };
    }
  }

  get #global(): EsmGlobalThis {
    return globalThis as unknown as EsmGlobalThis;
  }

  // Resolve against the page base so relative extension URLs work too. Never
  // throws: an unparseable URL just yields '' (capability fetch then denied).
  #originOf(url: string): string {
    try {
      return new URL(url, globalThis.location?.href).origin;
    } catch {
      return '';
    }
  }

  #getDependency(moduleName: string): unknown {
    return this.#getter?.(moduleName);
  }

  #installModuleCache(): void {
    Object.defineProperty(globalThis, this.#moduleCacheKey, {
      value: this.#cache,
      enumerable: false,
      writable: false,
      configurable: false
    });
  }

  /**
   * Register the es-module-shims resolve/fetch hooks. `resolve` maps registered
   * dependencies to a custom `ext-dep:` scheme; `fetch` serves them as generated
   * ES module source backed by the cache.
   */
  #registerHooks(): void {
    const resolve = (
      id: string,
      parentUrl: string,
      defaultResolve: (id: string, parentUrl: string) => string
    ): string => {
      // The extension component itself — keep the virtual URL as-is; the fetch
      // hook serves its source.
      if (id.startsWith('ext-main:')) {
        return id;
      }

      // Capabilities are served per-origin: scope them to the origin the
      // importing extension was loaded from (looked up by its ext-main parentUrl).
      if (id === capabilities.specifier) {
        const origin = this.#moduleOrigins.get(parentUrl) ?? '';
        return `ext-cap:${encodeURIComponent(origin)}`;
      }

      return this.#getDependency(id) !== undefined
        ? `ext-dep:${encodeURIComponent(id)}`
        : defaultResolve(id, parentUrl);
    };

    const fetchHook = async (
      url: string,
      options?: RequestInit
    ): Promise<Response> => {
      if (url.startsWith('ext-main:')) {
        const sourceId = url.slice('ext-main:'.length);
        return new Response(this.#mainSources.get(sourceId) ?? '', {
          headers: { 'content-type': 'text/javascript' }
        });
      }

      if (url.startsWith('ext-cap:')) {
        const origin = decodeURIComponent(url.slice('ext-cap:'.length));
        return this.#serveModule(capabilities.buildModule(origin));
      }

      if (url.startsWith('ext-dep:')) {
        const depName = decodeURIComponent(url.replace(/^ext-dep:/, ''));
        const dep = this.#getDependency(depName);

        if (dep !== undefined) {
          return this.#serveModule(dep);
        }
      }

      return fetch(url, options ?? {});
    };

    // Frozen so the resolve/fetch hooks cannot be swapped out after the fact
    // (e.g. to bypass the dependency whitelist or serve arbitrary modules).
    this.#global.esmsInitOptions = Object.freeze({
      shimMode: true,
      revokeBlobURLs: true,
      mapOverrides: true,
      resolve,
      fetch: fetchHook
    });
  }

  /** Cache a module value and serve generated ES source that re-exports it. */
  #serveModule(dep: unknown): Response {
    const cacheKey = `mod_${this.#cacheCounter++}`;
    this.#cache.set(cacheKey, dep);

    return new Response(this.#generateModuleSource(cacheKey, dep), {
      headers: { 'content-type': 'text/javascript' }
    });
  }

  /** Build ES module source that re-exports a cached dependency's value(s). */
  #generateModuleSource(cacheKey: string, dep: unknown): string {
    // Access the cache by its bare global name, NOT via `globalThis[...]`: a
    // served module may export a binding called `globalThis` (it's a capability),
    // and `export const globalThis = globalThis[...]` would shadow the real
    // global and hit a TDZ ("Cannot access 'globalThis' before initialization").
    // The cache key is a global property, so a bare reference resolves to it and
    // never collides with an export name.
    const ref = `${this.#moduleCacheKey}.get('${cacheKey}')`;

    if (typeof dep === 'object' && dep !== null && !Array.isArray(dep)) {
      return (
        Object.keys(dep)
          .map((key) =>
            key === 'default'
              ? `export default ${ref}?.default ?? ${ref}`
              : `export const ${key} = ${ref}?.[${JSON.stringify(key)}]`
          )
          .join(';\n') + ';'
      );
    }

    return `export default ${ref}`;
  }

  /** Load es-module-shims via a script tag and wait until importShim is ready. */
  #loadEsModuleShims(): Promise<void> {
    if (this.#global.importShim) {
      return Promise.resolve();
    }

    this.#esmsLoadPromise ??= new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.async = true;
      script.src = this.#esModuleShimsUrl;

      script.addEventListener('load', () => {
        if (this.#global.importShim) {
          resolve();
        } else {
          reject(
            new Error('es-module-shims loaded but importShim is not available')
          );
        }
      });

      script.addEventListener('error', () => {
        this.#esmsLoadPromise = undefined;
        reject(
          new Error(
            `Failed to load es-module-shims from ${this.#esModuleShimsUrl}`
          )
        );
      });

      document.head.appendChild(script);
    });

    return this.#esmsLoadPromise;
  }
}

// Singleton, instantiated at module load so the hooks are registered
// synchronously before es-module-shims loads.
export default new PrecompiledExtensionLoader();
